import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { CampaignBreadcrumbs } from "@/components/CampaignBreadcrumbs";
import { RelatedEntitiesPanel } from "@/components/RelatedEntitiesPanel";
import { RichTextEditor, RichTextDisplay } from "@/components/RichTextEditor";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { EditableField } from "@/components/character-sheet/EditableField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Castle, FileText, Building2, Users, Package, Flag, Target, ArrowLeft,
  ChevronRight, Plus, Image as ImageIcon, MapPin, Trash2, X, Pencil,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { key: "description", label: "Description", icon: FileText },
  { key: "buildings",   label: "Buildings",   icon: Building2 },
  { key: "npcs",        label: "NPCs",        icon: Users },
  { key: "items",       label: "Items",       icon: Package },
  { key: "factions",    label: "Factions",    icon: Flag },
  { key: "quests",      label: "Quests",      icon: Target },
];

const LocationDetailPage = () => {
  const { campaignId, locId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [location, setLocation] = useState(null);
  const [related, setRelated] = useState({ buildings: [], npcs: [], items: [], factions: [], quests: [] });
  const [regions, setRegions] = useState([]);
  const [activeSection, setActiveSection] = useState("description");
  const [loading, setLoading] = useState(true);
  const [savingTitle, setSavingTitle] = useState(false);
  // Building edit dialog — clicking a building card in the list opens this
  // (buildings have no dedicated route so we edit inline). null = closed.
  const [editingBuilding, setEditingBuilding] = useState(null);

  const fetchAll = useCallback(async () => {
    // Only show the full-page spinner on FIRST load (when we have no data yet).
    // Refetches (after save/create/delete of a nested entity) should NOT flip
    // the page back to a spinner — doing so unmounts any currently-open modal
    // (BuildingEditDialog) which drops its local state and can display stale
    // values on remount.
    setLocation(prev => {
      if (!prev) setLoading(true);
      return prev;
    });
    try {
      const [campRes, locRes, buildingsRes, npcsRes, itemsRes, factionsRes, questsRes, regionsRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/locations/${locId}`, { withCredentials: true }),
        axios.get(`${API}/locations/${locId}/buildings`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/npcs`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/items`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/factions`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/quests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/regions`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCampaign(campRes.data);
      setLocation(locRes.data);
      setRegions(regionsRes.data || []);
      setRelated({
        buildings: buildingsRes.data || [],
        npcs:     (npcsRes.data || []).filter(n => n.location_id === locId),
        items:    (itemsRes.data || []).filter(i => i.location_id === locId),
        factions: (factionsRes.data || []).filter(f => f.location_id === locId),
        quests:   (questsRes.data || []).filter(q => q.location_id === locId),
      });
    } catch {
      toast.error("Failed to load location");
      navigate(`/campaign/${campaignId}/locations`);
    } finally { setLoading(false); }
  }, [campaignId, locId, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveLocation = async (patch) => {
    const next = { ...location, ...patch };
    setLocation(next);
    setSavingTitle(true);
    try {
      await axios.put(`${API}/locations/${locId}`, {
        campaign_id: campaignId,
        name: next.name,
        description: next.description || "",
        location_type: next.location_type || "",
        region_id: next.region_id || "",
        map_id: next.map_id || null,
        visibility: next.visibility || "dm_only",
        visible_to: next.visible_to || [],
      }, { withCredentials: true });
    } catch { toast.error("Save failed"); }
    finally { setSavingTitle(false); }
  };

  // Local state to track image load failure — hides the broken <img> so the
  // fallback icon shows instead. Reset whenever the location's image_path
  // changes so a corrected upload can retry cleanly.
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [location?.image_path]);

  if (loading || !location) {
    return (
      <Layout campaignName={campaign?.name} hideBreadcrumbs>
        <CampaignBreadcrumbs campaignName={campaign?.name} />
        <div className="flex justify-center py-20"><div className="spinner" /></div>
      </Layout>
    );
  }

  const portrait = location.image_path?.startsWith("http")
    ? location.image_path
    : location.image_path ? `${process.env.REACT_APP_BACKEND_URL}${location.image_path}` : null;

  const counts = {
    buildings: related.buildings.length,
    npcs:      related.npcs.length,
    items:     related.items.length,
    factions:  related.factions.length,
    quests:    related.quests.length,
  };

  return (
    <Layout campaignName={campaign?.name} tabLabel={location.name} hideBreadcrumbs>
      <CampaignBreadcrumbs
        campaignName={campaign?.name}
        extraCrumbs={[
          { label: location.name },
        ]}
      />

      <div className="rounded-sm border border-border bg-card overflow-hidden" style={{ height: "calc(100vh - 200px)", minHeight: 600 }} data-testid="location-detail">
        <ResizablePanelGroup direction="horizontal">
          {/* LEFT — Location identity card */}
          <ResizablePanel defaultSize={20} minSize={15} maxSize={32}>
            <div className="h-full flex flex-col bg-[#110d0a]">
              <div className="px-3 py-2 border-b border-border flex items-center gap-2">
                <Castle className="w-3.5 h-3.5 text-primary" />
                <h3 className="text-xs uppercase tracking-[0.18em] text-muted-foreground/80 flex-1">Location</h3>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => navigate(`/campaign/${campaignId}/locations`)} title="Back" data-testid="location-back-btn">
                  <ArrowLeft className="w-3.5 h-3.5" />
                </Button>
              </div>

              <div className="p-4 space-y-3">
                {/* Portrait */}
                <div className="aspect-square bg-muted/20 border border-border rounded-sm overflow-hidden flex items-center justify-center" data-testid="location-portrait">
                  {portrait && !imgError ? (
                    // NOTE: no crossOrigin attribute. The image CDN echoes
                    // Access-Control-Allow-Origin: * (not the requesting
                    // origin) so browsers refuse the response when crossOrigin
                    // is set → broken image. Plain <img src> works because
                    // images are same-origin-policy exempt for rendering.
                    <img
                      src={portrait}
                      alt={location.name}
                      className="w-full h-full object-cover"
                      onError={() => setImgError(true)}
                    />
                  ) : (
                    <ImageIcon className="w-10 h-10 text-muted-foreground/20" />
                  )}
                </div>

                {/* Name */}
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1">Name</p>
                  <EditableField
                    value={location.name}
                    onSave={(v) => saveLocation({ name: v })}
                    className="text-base font-serif text-foreground w-full"
                    inputClassName="h-8 text-base font-serif"
                    data-testid="location-name-field"
                  />
                </div>

                {/* Type */}
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1">Type</p>
                  <EditableField
                    value={location.location_type}
                    onSave={(v) => saveLocation({ location_type: v })}
                    placeholder="City, Dungeon, Tavern…"
                    className="text-sm text-foreground w-full"
                    data-testid="location-type-field"
                  />
                </div>

                {/* Region */}
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Region</p>
                  <Select value={location.region_id || "none"} onValueChange={(v) => saveLocation({ region_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="h-8 text-sm bg-input border-border rounded-sm" data-testid="location-region-field">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="none">— No region —</SelectItem>
                      {regions.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <p className="text-[10px] text-muted-foreground/50 text-right">{savingTitle ? "Saving…" : "Saved"}</p>

                <div className="pt-3 border-t border-border">
                  <RelatedEntitiesPanel entityType="locations" entityId={location.id} />
                </div>
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* MIDDLE — section nav */}
          <ResizablePanel defaultSize={22} minSize={15} maxSize={35}>
            <div className="h-full flex flex-col bg-[#14100c]">
              <div className="px-3 py-2 border-b border-border flex items-center gap-2">
                <h3 className="text-xs uppercase tracking-[0.18em] text-muted-foreground/80 flex-1 truncate" data-testid="location-section-header">{location.name}</h3>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
                {SECTIONS.map((s) => {
                  const Icon = s.icon;
                  const active = activeSection === s.key;
                  const cnt = counts[s.key];
                  return (
                    <button
                      key={s.key}
                      onClick={() => setActiveSection(s.key)}
                      className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-sm transition-colors ${active ? "bg-primary/15 text-foreground" : "hover:bg-muted/30 text-muted-foreground"}`}
                      data-testid={`location-section-${s.key}`}
                    >
                      <ChevronRight className={`w-3 h-3 flex-shrink-0 ${active ? "text-primary" : "text-muted-foreground/40"}`} />
                      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${active ? "text-primary" : "text-muted-foreground/60"}`} />
                      <span className="flex-1 text-sm">{s.label}</span>
                      {cnt !== undefined && <span className="text-[10px] text-muted-foreground/60 tabular-nums">{cnt}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* RIGHT — section content */}
          <ResizablePanel defaultSize={58} minSize={40}>
            <div className="h-full flex flex-col bg-[#0d0a07] overflow-hidden">
              <div className="px-5 py-3 border-b border-border flex items-center gap-2">
                <h2 className="font-serif text-xl text-foreground flex-1 truncate" data-testid="location-content-title">
                  {SECTIONS.find(s => s.key === activeSection)?.label}
                </h2>
              </div>

              <div className="flex-1 overflow-y-auto p-5" data-testid={`location-content-${activeSection}`}>
                {activeSection === "description" && (
                  <div className="h-full min-h-[400px]">
                    <RichTextEditor
                      value={location.description || ""}
                      onChange={(v) => saveLocation({ description: v })}
                      placeholder="Describe this location — its history, atmosphere, secrets…"
                      campaignId={campaignId}
                      fillHeight
                    />
                  </div>
                )}

                {["buildings", "npcs", "items", "factions", "quests"].includes(activeSection) && (
                  <EntityList
                    section={activeSection}
                    items={related[activeSection]}
                    campaignId={campaignId}
                    locationId={locId}
                    onBuildingClick={(b) => setEditingBuilding(b)}
                  />
                )}
              </div>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Building edit dialog */}
      <BuildingEditDialog
        building={editingBuilding}
        open={!!editingBuilding}
        onOpenChange={(o) => { if (!o) setEditingBuilding(null); }}
        campaignId={campaignId}
        locationId={locId}
        onSaved={() => { fetchAll(); }}
        onDeleted={() => { setEditingBuilding(null); fetchAll(); }}
      />
    </Layout>
  );
};

const EntityList = ({ section, items, campaignId, locationId, onBuildingClick }) => {
  const navigate = useNavigate();
  // Per-section click behaviour:
  //   • buildings  — open inline edit dialog (no dedicated detail page)
  //   • items      — navigate to /campaign/:cid/item/:id (EntityDetailPage)
  //   • npcs       — navigate to /campaign/:cid/npcs/:id (NpcDetailPage, new
  //                  tab with view/edit toggle)
  //   • factions / quests — navigate to their section list pages (no
  //     dedicated detail routes today).
  const sectionListRoute = {
    factions: `/campaign/${campaignId}/factions`,
    quests:   `/campaign/${campaignId}/quests`,
  };
  const labelSingular = { buildings: "building", npcs: "NPC", items: "item", factions: "faction", quests: "quest" }[section];
  const emptyRoute = section === "items"
    ? `/campaign/${campaignId}/items`
    : section === "npcs"
      ? `/campaign/${campaignId}/npcs`
      : sectionListRoute[section];

  const handleClick = (it) => {
    if (section === "buildings") { onBuildingClick?.(it); return; }
    if (section === "items")     { navigate(`/campaign/${campaignId}/item/${it.id}`); return; }
    if (section === "npcs")      { navigate(`/campaign/${campaignId}/npcs/${it.id}`); return; }
    if (sectionListRoute[section]) { navigate(sectionListRoute[section]); return; }
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-muted-foreground/70 italic mb-3">No {section} linked to this location yet.</p>
        {emptyRoute && section !== "buildings" && (
          <Button variant="outline" size="sm" onClick={() => navigate(emptyRoute)} data-testid={`location-go-to-${section}`}>
            <Plus className="w-3 h-3 mr-1.5" /> Add {labelSingular} in {section}
          </Button>
        )}
        {section === "buildings" && (
          <Button variant="outline" size="sm" onClick={() => onBuildingClick?.({ id: null, name: "", building_type: "", description: "", campaign_id: campaignId, location_id: locationId })} data-testid="location-add-building">
            <Plus className="w-3 h-3 mr-1.5" /> Add building
          </Button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`location-list-${section}`}>
        {items.map((it) => (
          <button
            key={it.id}
            onClick={() => handleClick(it)}
            className="text-left p-3 rounded-sm border border-border bg-card hover:border-primary/60 hover:bg-primary/5 transition-colors"
            data-testid={`location-${section}-card-${it.id}`}
          >
            <p className="text-sm font-medium text-foreground mb-1 truncate">{it.name || it.title}</p>
            {it.description && (
              <div className="text-xs text-muted-foreground line-clamp-2 prose-sm max-w-none">
                <RichTextDisplay content={it.description} />
              </div>
            )}
            {it.building_type && <span className="text-[10px] uppercase tracking-wider text-primary/70 mt-1 inline-block">{it.building_type}</span>}
            {it.race && <span className="text-[10px] uppercase tracking-wider text-primary/70 mt-1 inline-block">{it.race}</span>}
          </button>
        ))}
      </div>
      {section === "buildings" && (
        <div className="mt-4 flex justify-center">
          <Button variant="outline" size="sm" onClick={() => onBuildingClick?.({ id: null, name: "", building_type: "", description: "", campaign_id: campaignId, location_id: locationId })} data-testid="location-add-building">
            <Plus className="w-3 h-3 mr-1.5" /> Add building
          </Button>
        </div>
      )}
    </div>
  );
};

// Inline building viewer/editor. Opens in read-only view mode by default; the
// Edit button flips to editable input+RichTextEditor mode. Handles both create
// (building.id === null → starts in edit mode) and update.
const BuildingEditDialog = ({ building, open, onOpenChange, campaignId, locationId, onSaved, onDeleted }) => {
  const [name, setName] = useState("");
  const [buildingType, setBuildingType] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("view"); // "view" | "edit"

  useEffect(() => {
    if (!open || !building) return;
    setName(building.name || "");
    setBuildingType(building.building_type || "");
    setDescription(building.description || "");
    // New buildings must open directly in edit mode (nothing to view yet).
    setMode(building.id ? "view" : "edit");
  }, [open, building]);

  if (!building) return null;
  const isNew = !building.id;
  const isEdit = mode === "edit";

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const body = {
        campaign_id: campaignId,
        location_id: locationId,
        name: name.trim(),
        building_type: buildingType,
        description,
      };
      if (isNew) {
        await axios.post(`${API}/buildings`, body, { withCredentials: true });
        toast.success("Building created");
        onSaved?.();
        onOpenChange(false);
      } else {
        const res = await axios.put(`${API}/buildings/${building.id}`, body, { withCredentials: true });
        toast.success("Building saved");
        // Refresh local state from the server response so view mode shows the
        // just-saved values regardless of parent-side refresh timing.
        if (res?.data) {
          setName(res.data.name || "");
          setBuildingType(res.data.building_type || "");
          setDescription(res.data.description || "");
        }
        onSaved?.();
        // Stay in the dialog but drop back to view mode so the user sees the
        // saved-and-formatted state immediately.
        setMode("view");
      }
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const cancelEdit = () => {
    // Reset edits back to the last-saved values, then flip to view mode.
    setName(building.name || "");
    setBuildingType(building.building_type || "");
    setDescription(building.description || "");
    setMode("view");
  };

  const remove = async () => {
    if (isNew) { onOpenChange(false); return; }
    if (!window.confirm(`Delete "${building.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/buildings/${building.id}`, { withCredentials: true });
      toast.success("Building deleted");
      onDeleted?.();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-card border-border" data-testid="building-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center justify-between">
            <span>{isNew ? "New Building" : (isEdit ? `Editing: ${building.name}` : building.name)}</span>
            {!isNew && !isEdit && (
              <Button size="sm" variant="outline" onClick={() => setMode("edit")} data-testid="building-enter-edit-btn">
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {isEdit ? (
          <div className="space-y-4 py-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1 block">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Building name" data-testid="building-name-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1 block">Type</label>
              <Input value={buildingType} onChange={(e) => setBuildingType(e.target.value)} placeholder="Tavern, Shop, Temple, Library…" data-testid="building-type-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1 block">Description</label>
              <div className="min-h-[240px] border border-border rounded-sm">
                <RichTextEditor
                  value={description}
                  onChange={setDescription}
                  placeholder="Describe this building — its purpose, atmosphere, patrons, secrets…"
                  campaignId={campaignId}
                  fillHeight
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2" data-testid="building-view-mode">
            {buildingType && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1">Type</p>
                <p className="text-sm text-primary">{buildingType}</p>
              </div>
            )}
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-2">Description</p>
              {description ? (
                <div className="prose prose-sm dark:prose-invert max-w-none border border-border/60 rounded-sm px-4 py-3 bg-muted/10 min-h-[180px]">
                  <RichTextDisplay content={description} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground/60 italic border border-dashed border-border rounded-sm px-4 py-6 text-center">No description yet — click Edit to add one.</p>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="flex sm:justify-between gap-2">
          <div>
            {!isNew && isEdit && (
              <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={remove} data-testid="building-delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {isEdit ? (
              <>
                <Button variant="outline" size="sm" onClick={isNew ? () => onOpenChange(false) : cancelEdit} data-testid="building-cancel-btn">
                  <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
                </Button>
                <Button size="sm" onClick={save} disabled={saving || !name.trim()} data-testid="building-save-btn">
                  {saving ? "Saving…" : (isNew ? "Create" : "Save")}
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="building-close-btn">
                Close
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LocationDetailPage;
